/**
 * Step 3 — SFOP Impact Matrix (Story 15.B port from legacy
 * `features/athena/components/StepSummary/ImpactMatrix.jsx`).
 *
 * Renders four donut charts (one per SFOP category — Safety,
 * Financial, Operational, Privacy) showing the distribution of
 * severity levels (Severe / Major / Moderate / Minor / Negligible)
 * across the current step's damage scenarios. A footer surfaces
 * the top 3 highest-impact scenarios for quick triage.
 *
 * Computation reused verbatim from the legacy implementation
 * (segment math, level taxonomy, worst-case filter). Chrome
 * authored fresh against v2 design tokens — no hex literals, no
 * animation beyond the existing 150ms ease on the collapse
 * chevron. Colors via `getImpactColor(level, { scheme: 'fiveTier' })`.
 */
import React, { useMemo, useState } from 'react';
import type { ScenarioRecord } from './scenarioFieldSchema';
import { getImpactColor } from '../../../../utils/severityStyle';
import { Icon } from '../../../../components/ui/Icon';
import styles from './ImpactMatrix.module.css';

const CATEGORIES = ['Safety', 'Financial', 'Operational', 'Privacy'] as const;
type Category = (typeof CATEGORIES)[number];

const LEVELS = ['Severe', 'Major', 'Moderate', 'Minor', 'Negligible'] as const;
type Level = (typeof LEVELS)[number];

type CategoryStats = Record<Level, number>;
type AllStats = Record<Category, CategoryStats>;

interface PieSegment {
  level: Level;
  count: number;
  fraction: number;
  offset: number;
}

const PIE_RADIUS = 30;
const PIE_CIRC = 2 * Math.PI * PIE_RADIUS;
const PIE_STROKE = 14;

/** Match a scenario's per-category field to the canonical capital-case
 *  level. Tolerates the legacy lowercase / `none` variants by lifting
 *  them into the canonical taxonomy. */
function normalizeLevel(raw: unknown): Level | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  if (lower === 'severe') return 'Severe';
  if (lower === 'major') return 'Major';
  if (lower === 'moderate') return 'Moderate';
  if (lower === 'minor') return 'Minor';
  if (lower === 'negligible' || lower === 'none') return 'Negligible';
  return null;
}

function calculateImpactStats(scenarios: ScenarioRecord[]): AllStats {
  const stats: AllStats = {
    Safety:      { Severe: 0, Major: 0, Moderate: 0, Minor: 0, Negligible: 0 },
    Financial:   { Severe: 0, Major: 0, Moderate: 0, Minor: 0, Negligible: 0 },
    Operational: { Severe: 0, Major: 0, Moderate: 0, Minor: 0, Negligible: 0 },
    Privacy:     { Severe: 0, Major: 0, Moderate: 0, Minor: 0, Negligible: 0 },
  };

  for (const s of scenarios) {
    const row = s as Record<string, unknown>;
    const reads: Record<Category, unknown> = {
      Safety:      row.safety ?? row.impact_safety,
      Financial:   row.financial ?? row.impact_financial,
      Operational: row.operational ?? row.impact_operational,
      Privacy:     row.privacy ?? row.impact_privacy,
    };
    for (const category of CATEGORIES) {
      const level = normalizeLevel(reads[category]);
      if (level) stats[category][level] += 1;
    }
  }
  return stats;
}

function buildPieSegments(stats: CategoryStats, total: number): PieSegment[] {
  const segments: PieSegment[] = [];
  let offset = 0;
  for (const level of LEVELS) {
    const count = stats[level];
    if (count === 0) continue;
    const fraction = total > 0 ? count / total : 0;
    segments.push({ level, count, fraction, offset });
    offset += fraction;
  }
  return segments;
}

export interface ImpactMatrixProps {
  scenarios: ScenarioRecord[];
}

export default function ImpactMatrix({ scenarios }: ImpactMatrixProps) {
  // Collapsed by default; state is not persisted, so it resets on every
  // remount of this step.
  const [collapsed, setCollapsed] = useState<boolean>(true);

  const toggle = () => setCollapsed((prev) => !prev);

  const stats = useMemo(() => calculateImpactStats(scenarios), [scenarios]);

  const worstCase = useMemo(
    () =>
      scenarios
        .filter((s: any) => {
          const r = s?.final_impact_rating ?? s?.severity;
          return r === 'Severe' || r === 'Major';
        })
        .slice(0, 3),
    [scenarios],
  );

  const overallTotal = CATEGORIES.reduce(
    (acc, c) => acc + LEVELS.reduce((sum, l) => sum + stats[c][l], 0),
    0,
  );

  return (
    <section className={styles.card}>
      <button
        type="button"
        className={styles.head}
        onClick={toggle}
        aria-expanded={!collapsed}
      >
        <span className={styles.headLeft}>
          <span className={styles.title}>SFOP Impact Distribution</span>
          <span className={styles.subtitle}>· {overallTotal} ratings across {scenarios.length} scenarios</span>
        </span>
        <span
          className={`${styles.chevron} ${collapsed ? styles.chevronCollapsed : ''}`}
        >
          <Icon name="chevron-down" size={14} />
        </span>
      </button>

      {!collapsed && (
        <div className={styles.body}>
          <div className={styles.categoryGrid}>
            {CATEGORIES.map((category) => {
              const categoryStats = stats[category];
              const categoryTotal = LEVELS.reduce(
                (sum, l) => sum + categoryStats[l],
                0,
              );
              const segments = buildPieSegments(categoryStats, categoryTotal);

              return (
                <div key={category} className={styles.category}>
                  <div className={styles.categoryHeader}>
                    <span className={styles.categoryName}>{category}</span>
                    <span className={styles.categoryTotal}>{categoryTotal}</span>
                  </div>

                  {categoryTotal > 0 ? (
                    <>
                      <div className={styles.chartWrapper}>
                        <svg width="80" height="80" viewBox="0 0 80 80">
                          <circle
                            cx="40" cy="40" r={PIE_RADIUS}
                            fill="none"
                            stroke="var(--border)"
                            strokeWidth={PIE_STROKE}
                          />
                          {segments.map(({ level, fraction, offset }) => (
                            <circle
                              key={level}
                              cx="40" cy="40" r={PIE_RADIUS}
                              fill="none"
                              stroke={getImpactColor(level, { scheme: 'fiveTier' }) ?? 'var(--muted-foreground)'}
                              strokeWidth={PIE_STROKE}
                              strokeDasharray={`${fraction * PIE_CIRC} ${PIE_CIRC}`}
                              strokeDashoffset={-(offset * PIE_CIRC)}
                              transform="rotate(-90, 40, 40)"
                              strokeLinecap="butt"
                            />
                          ))}
                          <text
                            x="40" y="45"
                            textAnchor="middle"
                            className={styles.donutCenter}
                          >
                            {categoryTotal}
                          </text>
                        </svg>
                      </div>

                      <div className={styles.legend}>
                        {LEVELS.map((level) => {
                          const count = categoryStats[level];
                          if (count === 0) return null;
                          return (
                            <div key={level} className={styles.legendItem}>
                              <span
                                className={styles.legendDot}
                                style={{ background: getImpactColor(level, { scheme: 'fiveTier' }) ?? 'var(--muted-foreground)' }}
                              />
                              <span className={styles.legendLabel}>{level}</span>
                              <span className={styles.legendCount}>{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className={styles.emptyCategory}>No impact data</div>
                  )}
                </div>
              );
            })}
          </div>

          {worstCase.length > 0 && (
            <div className={styles.worstCase}>
              <div className={styles.worstCaseHead}>Highest Impact Scenarios</div>
              <div className={styles.worstCaseList}>
                {worstCase.map((s: any) => {
                  const rating = s?.final_impact_rating ?? s?.severity ?? '';
                  return (
                    <div key={s.damage_id} className={styles.worstCaseRow}>
                      <span className={styles.worstCaseId}>{s.damage_id}</span>
                      <span className={styles.worstCaseScenario}>
                        {s.damage_scenario ?? ''}
                      </span>
                      <span
                        className={styles.worstCaseSeverity}
                        style={{ color: getImpactColor(rating, { scheme: 'fiveTier' }) ?? 'var(--foreground)' }}
                      >
                        {rating}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
